import { PageHeader } from '@/components/PageHeader/PageHeader.tsx'
import { PageLoadingSpinner } from '@/components/PageLoadingSpinner/PageLoadingSpinner.tsx'
import { PermissionGuard } from '@/components/PermissionGuard.tsx'
import { QueryLoader } from '@/components/QueryLoader/QueryLoader.tsx'
import { useCompany } from '@/contexts/CompaniesContext.tsx'
import { type RoleList, type UserRoleType } from '@/features/user/api'
import { UserList, type UserMenuProps } from '@/features/user/components/UserList.tsx'
import { UserRoleIcon } from '@/features/user/components/UserRoleIcon.tsx'
import { useReadableRoles } from '@/features/user/hooks/useReadableRoles.ts'
import { FEATURES, isFeatureEnabled } from '@/utils/feature-flag.ts'
import AddIcon from '@mui/icons-material/Add'
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined'
import { Box, Button, Stack, type TabOwnProps } from '@mui/material'
import { useMemo } from 'react'
import {
  createFileRoute,
  createLink,
  redirect,
  retainSearchParams
} from '@tanstack/react-router'
import { z } from 'zod'
import { CoercedStringArray } from '@/utils/common.schema'
import { useTranslation } from 'react-i18next'
import { useInvitableRoles } from '@/features/user/hooks'
import type { UserEmployee } from '@/features/user/api/user-employee.data.ts'
import { CustomMenu } from '@/components/Menu/CustomMenu.tsx'
import BorderColorTwoToneIcon from '@mui/icons-material/BorderColorTwoTone'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import { useAuth } from '@/contexts'
import { AppTabs } from '@/components/Tab/AppTab.tsx'
import type { TFunction } from 'i18next'

const SearchSchema = z.object({
  role: CoercedStringArray.optional(),
  status: CoercedStringArray.optional(),
  site: CoercedStringArray.optional()
})

export const Route = createFileRoute('/_authenticated/$companySlug/users/_overview')({
  validateSearch: SearchSchema,
  search: {
    middlewares: [retainSearchParams(['role', 'status', 'site'])]
  },
  beforeLoad: ({ context }) => {
    // Check if user management feature is enabled
    if (!isFeatureEnabled(FEATURES.USER_MANAGEMENT)) {
      throw redirect({
        to: '/not-found'
      })
    }
    context.auth.rejectIfMissingPermissions(['USER_READ'])
  },
  pendingComponent: Loader,
  component: UserOverview
})

function Loader() {
  return <PageLoadingSpinner key="page-loader" title="Loading users..." />
}

const LinkButton = createLink(Button)

const supportedRoleTabs: UserRoleType[] = [
  'DRIVER',
  'DISPATCHER',
  'FLEET_MANAGER'
] as const

function removeUnsupportedTabRoles(roles: RoleList): UserRoleType[] {
  return roles.roles.filter(userRole => supportedRoleTabs.includes(userRole))
}

function UserDropdownMenu({ anchorEl, open, onClose, user }: UserMenuProps) {
  const { t } = useTranslation('@feature_user')
  const navigate = Route.useNavigate()

  const auth = useAuth()
  const { access } = useCompany()
  const writableRoles = useInvitableRoles()

  const canEditUser =
    auth.status == 'AUTHENTICATED' &&
    auth.hasPermissions(['USER_WRITE']) &&
    access == 'COMPANY_WIDE' &&
    writableRoles.data &&
    writableRoles.data.roles.includes(user.role)

  const handleDrawerSelectedUser = (user: UserEmployee) => {
    onClose?.()

    // Navigate to the user detail route
    navigate({
      to: '/$companySlug/users/$userId',
      params: { userId: user.id }
    })
  }

  const handleUserUpdate = (user: UserEmployee) => {
    onClose?.()

    // Navigate to the user update route
    navigate({
      to: '/$companySlug/users/$userId/edit',
      params: { userId: user.id }
    })
  }

  const listMenuItems = [
    {
      label: t('components.UserList.actions.viewDetails'),
      icon: VisibilityOutlinedIcon,
      onClick: () => handleDrawerSelectedUser(user)
    },
    canEditUser && {
      label: t('components.UserList.actions.edit'),
      icon: BorderColorTwoToneIcon,
      onClick: () => handleUserUpdate(user)
    }
  ].filter(it => !!it)

  return (
    <CustomMenu
      id={'user-menu'}
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      items={listMenuItems}
    />
  )
}

function UserTabs({ readableRoles }: { readableRoles: RoleList }) {
  const { t } = useTranslation(['_routes', '_global', '@feature_user'])
  const selectedSitesIds = Route.useSearch({ select: s => s.site })
  const { sites } = useCompany()

  const tabs = useMemo(
    () =>
      getTabsFromReadableRoles(t, readableRoles).map(section => ({
        label: section.label,
        value: section.value,
        icon: section.icon,
        content: () => (
          <UserList
            sites={sites}
            availableRoles={section.role ? [section.role] : readableRoles.roles}
            roleFilter={section.role}
            selectedSites={selectedSitesIds}
            userMenu={UserDropdownMenu}
          />
        )
      })),
    [t, readableRoles, sites, selectedSitesIds]
  )

  return <AppTabs tabs={tabs} />
}

function UserOverview() {
  const { t } = useTranslation(['_routes', '_global', '@feature_user'])
  const readableRolesQuery = useReadableRoles()

  return (
    <Stack spacing={4}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <PageHeader>{t('routes.users.main.title')}</PageHeader>

        <PermissionGuard permissions={['USER_WRITE', 'USER_READ']}>
          <LinkButton
            variant="contained"
            from={Route.fullPath}
            to="/$companySlug/users/create"
            startIcon={<AddIcon />}
          >
            {t('routes.users.main.buttons.creation')}
          </LinkButton>
        </PermissionGuard>
      </Box>

      <QueryLoader query={readableRolesQuery}>
        {readableRoles => <UserTabs readableRoles={readableRoles} />}
      </QueryLoader>
    </Stack>
  )
}

function getTabsFromReadableRoles(
  t: TFunction<readonly ['_routes', '_global', '@feature_user'], undefined>,
  readableRoles: RoleList
) {
  const tabItems: {
    label: string
    value: string
    icon: TabOwnProps['icon']
    role?: UserRoleType
  }[] = [
    {
      label: t('routes.users.main.tab.all'),
      value: 'all-roles',
      icon: <PersonOutlineOutlinedIcon sx={{ mr: 1 }} />
    }
  ]

  const extraTabs = removeUnsupportedTabRoles(readableRoles).map(role => ({
    label: t(`@feature_user:api.data.roles.${role}`),
    value: `role-${role}`,
    icon: <UserRoleIcon userRole={role} sx={{ mr: 1 }} />,
    role: role
  }))

  tabItems.push(...extraTabs)

  return tabItems
}
